import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { PsvProviderStatus } from "@api/shared-runtime/psv-review";
import {
  PSV_PRESENTATION,
  psvStatusDetail,
  psvStatusLabel,
  psvTitle,
  type PsvStatus,
} from "./workspace-psv-presentation";

const PERCENT = 100;

export function WorkspacePsvBadge({ psv }: { psv: PsvStatus }) {
  const { icon: Icon, badgeClassName } = PSV_PRESENTATION[psv.status];
  const spinning = psv.status === PsvProviderStatus.InProgress && !psv.queued;
  return (
    <Badge
      variant="outline"
      title={psvTitle(psv)}
      className={cn("gap-1 whitespace-nowrap", badgeClassName)}
    >
      <Icon
        className={cn(
          "size-3.5 shrink-0",
          spinning && "motion-safe:animate-spin",
        )}
        aria-hidden="true"
      />
      {psvStatusLabel(psv)}
    </Badge>
  );
}

/** The roster's verification cell: status badge, what it needs, and while
 * checks run, how far they have come. */
export function WorkspacePsvStatusCell({ psv }: { psv: PsvStatus }) {
  const running = psv.status === PsvProviderStatus.InProgress && !psv.queued;
  const progress =
    psv.totalChecks > 0
      ? Math.min(PERCENT, (psv.finishedChecks / psv.totalChecks) * PERCENT)
      : 0;
  return (
    <div className="flex min-w-40 flex-col items-start gap-1.5">
      <WorkspacePsvBadge psv={psv} />
      <span className="text-xs text-muted-foreground">
        {psvStatusDetail(psv)}
      </span>
      {running && psv.totalChecks > 0 && (
        <div
          role="progressbar"
          aria-label="Checks finished"
          aria-valuemin={0}
          aria-valuemax={psv.totalChecks}
          aria-valuenow={psv.finishedChecks}
          className="h-1 w-32 overflow-hidden rounded-full bg-blue-100"
        >
          <div
            className="h-full rounded-full bg-blue-600"
            style={{ width: `${progress}%` }}
          />
        </div>
      )}
    </div>
  );
}
